import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';

const UserMenu = ({ userName, onLogout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isWhitelisted, setIsWhitelisted] = useState(false);
  const [isCheckingWhitelist, setIsCheckingWhitelist] = useState(true);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  // Verificar se usuário está na whitelist ao carregar componente
  useEffect(() => {
    const checkWhitelist = async () => {
      try {
        setIsCheckingWhitelist(true);
        const hasAccess = await apiService.verificarWhitelist();
        setIsWhitelisted(hasAccess);
      } catch (error) {
        // Se der erro, assume que não está na whitelist
        setIsWhitelisted(false);
      } finally {
        setIsCheckingWhitelist(false);
      }
    };
    
    checkWhitelist();
  }, []);


  // Fechar menu ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen) {
        // Verificar se o clique foi fora do menu
        const dropdown = document.querySelector('[data-dropdown="user-menu"]');
        if (dropdown && !dropdown.contains(event.target) && !menuRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleLogout = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(false);
    if (onLogout && typeof onLogout === 'function') {
      onLogout();
    }
  };


  // Obter posição do botão para posicionar o dropdown
  const getButtonPosition = () => {
    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      return {
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX
      };
    }
    return { top: 0, left: 0 };
  };

  return (
    <div className="relative" ref={menuRef}>
      {/* Botão do usuário */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-1.5 rounded-lg transition-all duration-200 px-4 py-2 group hover:bg-gray-50"
      >
        {/* Avatar com ícone de pessoa */}
        <div className="w-7 h-7 rounded-full flex items-center justify-center group-hover:scale-110 transition-all duration-200">
          <svg 
            className="w-5 h-5 text-gray-600 group-hover:text-gray-800" 
            fill="currentColor" 
            viewBox="0 0 20 20"
          >
            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
          </svg>
        </div>
        
        {/* Nome do usuário */}
        <div className="text-left hidden sm:block">
          <p className="text-sm font-semibold text-gray-800 truncate max-w-[120px]" style={{ color: '#1f2937' }}>
            {userName || 'Usuário'}
          </p>
        </div>

        {/* Ícone de seta - indicador de dropdown */}
        <svg
          className={`w-4 h-4 text-gray-500 transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu usando Portal */}
      {isOpen && createPortal(
        <div 
          data-dropdown="user-menu"
          className="fixed bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden"
          style={{
            top: getButtonPosition().top,
            left: getButtonPosition().left,
            zIndex: 99999,
            minWidth: '180px'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Opção Solicitações de Projetos (apenas se estiver na whitelist) */}
          {!isCheckingWhitelist && isWhitelisted && (
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/solicitacoes');
              }}
              className="w-full px-4 py-2 text-left flex items-center space-x-3 hover:bg-blue-50 transition-colors duration-150 border-b border-gray-200"
            >
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="text-sm font-medium text-gray-800" style={{ color: '#1f2937' }}>Solicitações</span>
            </button>
          )}
          
          {/* Botão de Logout */}
          <button
            onClick={handleLogout}
            className="w-full px-4 py-2 text-left flex items-center space-x-3 hover:bg-red-50 transition-colors duration-150"
          >
            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span className="text-sm font-medium text-gray-800" style={{ color: '#1f2937' }}>Sair</span>
          </button>
        </div>,
        document.body
      )}
    </div>
  );
};

export default UserMenu;
